import { createSlice } from '@reduxjs/toolkit';

import type { CitacaoArbitralState } from '../types/portalArbitralTypes';

import {
  arbitragemGetDetalhe,
  arbitragemSolicitarTrocaEspecialista,
  convidarAdvogadoPorEmail,
  arbitragemResolverPendencia,
  citacaoArbitralGetDetail,
  citacaoArbitralGetFeed,
  citacaoArbitralList,
  citacaoArbitralRequestMagicLink,
  citacaoArbitralSubmit,
  citacaoArbitralValidateHash,
} from './actions';

const initialState: CitacaoArbitralState = {
  convidarAdvogado: { loading: false, error: null },
  trocarEspecialista: { loading: false, error: null },
  validateHash: { loading: false, error: null, response: null },
  requestMagicLink: { loading: false, error: null },
  list: { loading: false, error: null, items: [] },
  detail: { loading: false, error: null, data: null },
  arbitragem: { loading: false, error: null, data: null },
  resolverPendencia: { pendenciaId: null, error: null },
  submit: { loading: false, error: null, response: null },
  feed: { loading: false, error: null, arbitral: [], extrajudicial: [] },
};

const citacaoArbitralSlice = createSlice({
  name: 'citacaoArbitral',
  initialState,
  reducers: {
    resetSubmit(state) {
      state.submit = { loading: false, error: null, response: null };
    },
    resetDetail(state) {
      state.detail = { loading: false, error: null, data: null };
    },
    resetArbitragem(state) {
      state.arbitragem = { loading: false, error: null, data: null };
      state.resolverPendencia = { pendenciaId: null, error: null };
    },
    limparErroPendencia(state) {
      state.resolverPendencia.error = null;
    },
    limparErroConvidarAdvogado(state) {
      state.convidarAdvogado.error = null;
    },
    limparErroTrocarEspecialista(state) {
      state.trocarEspecialista.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(convidarAdvogadoPorEmail.pending, (state) => {
        state.convidarAdvogado.loading = true;
        state.convidarAdvogado.error = null;
      })
      .addCase(convidarAdvogadoPorEmail.fulfilled, (state) => {
        state.convidarAdvogado.loading = false;
      })
      .addCase(convidarAdvogadoPorEmail.rejected, (state, action) => {
        state.convidarAdvogado.loading = false;
        state.convidarAdvogado.error = action.payload ?? 'Não foi possível enviar o convite';
      })
      .addCase(arbitragemSolicitarTrocaEspecialista.pending, (state) => {
        state.trocarEspecialista.loading = true;
        state.trocarEspecialista.error = null;
      })
      .addCase(arbitragemSolicitarTrocaEspecialista.fulfilled, (state) => {
        state.trocarEspecialista.loading = false;
      })
      .addCase(arbitragemSolicitarTrocaEspecialista.rejected, (state, action) => {
        state.trocarEspecialista.loading = false;
        state.trocarEspecialista.error = action.payload ?? 'Não foi possível enviar a solicitação';
      })
      .addCase(citacaoArbitralValidateHash.pending, (state) => {
        state.validateHash.loading = true;
        state.validateHash.error = null;
      })
      .addCase(citacaoArbitralValidateHash.fulfilled, (state, action) => {
        state.validateHash.loading = false;
        state.validateHash.response = action.payload;
      })
      .addCase(citacaoArbitralValidateHash.rejected, (state, action) => {
        state.validateHash.loading = false;
        state.validateHash.error = action.payload ?? 'Erro desconhecido';
      })

      .addCase(citacaoArbitralRequestMagicLink.pending, (state) => {
        state.requestMagicLink.loading = true;
        state.requestMagicLink.error = null;
      })
      .addCase(citacaoArbitralRequestMagicLink.fulfilled, (state) => {
        state.requestMagicLink.loading = false;
      })
      .addCase(citacaoArbitralRequestMagicLink.rejected, (state, action) => {
        state.requestMagicLink.loading = false;
        state.requestMagicLink.error = action.payload ?? 'Erro desconhecido';
      })

      .addCase(citacaoArbitralList.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(citacaoArbitralList.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.items = action.payload;
      })
      .addCase(citacaoArbitralList.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload ?? 'Erro desconhecido';
      })

      .addCase(citacaoArbitralGetDetail.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(citacaoArbitralGetDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data = action.payload;
      })
      .addCase(citacaoArbitralGetDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload ?? 'Erro desconhecido';
      })

      .addCase(arbitragemGetDetalhe.pending, (state) => {
        state.arbitragem.loading = true;
        state.arbitragem.error = null;
      })
      // TODO(#1410): descartar a resposta de um GET que não é o corrente (troca de caso com GET em
      // voo grava o detalhe do caso anterior sob a URL do novo).
      .addCase(arbitragemGetDetalhe.fulfilled, (state, action) => {
        state.arbitragem.loading = false;
        state.arbitragem.data = action.payload;
      })
      .addCase(arbitragemGetDetalhe.rejected, (state, action) => {
        state.arbitragem.loading = false;
        state.arbitragem.error = action.payload ?? 'Erro desconhecido';
      })

      .addCase(arbitragemResolverPendencia.pending, (state, action) => {
        state.resolverPendencia.pendenciaId = action.meta.arg.pendencia.id;
        state.resolverPendencia.error = null;
      })
      .addCase(arbitragemResolverPendencia.fulfilled, (state, action) => {
        state.resolverPendencia.pendenciaId = null;
        const detalhe = state.arbitragem.data;
        if (!detalhe) return;

        const { resolvida, andamento } = action.payload;
        detalhe.pendencias = detalhe.pendencias.filter(
          (pendencia) => pendencia.id !== resolvida.id,
        );
        detalhe.pendenciasResolvidas = [
          resolvida,
          ...detalhe.pendenciasResolvidas.filter((item) => item.id !== resolvida.id),
        ];
        const novoAndamento = { ...andamento, numero: detalhe.andamentos.length + 1 };
        detalhe.andamentos = [novoAndamento, ...detalhe.andamentos];
        // `etapas` NÃO é recomputada aqui: as sete macroetapas fixas (ClickUp 86ak8mece) vêm do
        // `etapaAtual` que o BACKEND deriva, e resolver uma pendência não move essa macroetapa
        // por si só — o próximo refetch completo traz a atualizada, se houver.
      })
      .addCase(arbitragemResolverPendencia.rejected, (state, action) => {
        state.resolverPendencia.pendenciaId = null;
        state.resolverPendencia.error =
          action.payload ?? 'Não foi possível enviar sua resposta. Tente novamente.';
      })

      .addCase(citacaoArbitralSubmit.pending, (state) => {
        state.submit.loading = true;
        state.submit.error = null;
      })
      .addCase(citacaoArbitralSubmit.fulfilled, (state, action) => {
        state.submit.loading = false;
        state.submit.response = action.payload;
      })
      .addCase(citacaoArbitralSubmit.rejected, (state, action) => {
        state.submit.loading = false;
        state.submit.error = action.payload ?? 'Erro desconhecido';
      })

      .addCase(citacaoArbitralGetFeed.pending, (state) => {
        state.feed.loading = true;
        state.feed.error = null;
      })
      .addCase(citacaoArbitralGetFeed.fulfilled, (state, action) => {
        state.feed.loading = false;
        state.feed.arbitral = action.payload.arbitral;
        const extra = action.payload.extrajudicial;
        state.feed.extrajudicial = extra.items ?? extra.data ?? [];
      })
      .addCase(citacaoArbitralGetFeed.rejected, (state, action) => {
        state.feed.loading = false;
        state.feed.error = action.payload ?? 'Erro desconhecido';
      });
  },
});

export const {
  resetSubmit,
  resetDetail,
  resetArbitragem,
  limparErroPendencia,
  limparErroConvidarAdvogado,
  limparErroTrocarEspecialista,
} = citacaoArbitralSlice.actions;
export default citacaoArbitralSlice.reducer;
